import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: 'Sarah Chen',
      role: 'Senior Engineering Manager',
      company: 'TechFlow Inc',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      content: `MessagingHub Pro transformed our customer support platform. The real-time messaging capabilities and robust APIs allowed us to build a seamless experience for our 50,000+ daily users. The integration was surprisingly smooth, and their support team was exceptional.`,
      rating: 5,
      metrics: '50,000+ daily users',
      useCase: 'Customer Support Platform'
    },
    {
      id: 2,
      name: 'Marcus Rodriguez',
      role: 'CTO',
      company: 'StartupXYZ',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      content: `As a startup, we needed a messaging solution that could scale with us. MessagingHub Pro's pricing model and feature set were perfect. We went from prototype to production in just 2 weeks. The developer experience is outstanding.`,rating: 5,metrics: '2 weeks to production',useCase: 'Team Collaboration App'
    },
    {
      id: 3,
      name: 'Emily Watson',role: 'Lead Developer',company: 'Enterprise Solutions Corp',avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      content: `The enterprise security features and compliance certifications were crucial for our healthcare application. MessagingHub Pro met all our HIPAA requirements while providing the performance we needed for real-time patient communication.`,
      rating: 5,
      metrics: 'HIPAA compliant',useCase: 'Healthcare Communication'
    },
    {
      id: 4,
      name: 'David Kim',role: 'Product Manager',company: 'GameDev Studios',avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      content: `Our multiplayer game needed ultra-low latency messaging for real-time gameplay. MessagingHub Pro's global infrastructure and sub-50ms response times made our game feel incredibly responsive. Player engagement increased by 40%.`,
      rating: 5,
      metrics: '40% engagement increase',
      useCase: 'Gaming Platform'
    },
    {
      id: 5,
      name: 'Lisa Thompson',
      role: 'VP of Engineering',
      company: 'FinTech Innovations',
      avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face',
      content: `Security and reliability are non-negotiable in fintech. MessagingHub Pro's SOC 2 compliance and 99.9% uptime guarantee gave us the confidence to build our trading platform's communication layer on their infrastructure.`,
      rating: 5,
      metrics: '99.9% uptime achieved',
      useCase: 'Financial Trading Platform'
    }
  ];

  const companies = [
    { name: 'TechFlow Inc', logo: 'Building' },
    { name: 'StartupXYZ', logo: 'Rocket' },
    { name: 'Enterprise Corp', logo: 'Building2' },
    { name: 'GameDev Studios', logo: 'Gamepad2' },
    { name: 'FinTech Innovations', logo: 'TrendingUp' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonials?.length - 1 ? 0 : prevIndex + 1
      );
    }, 6000);

    return () => clearInterval(interval);
  }, [testimonials?.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const nextSlide = () => {
    setCurrentIndex(currentIndex === testimonials?.length - 1 ? 0 : currentIndex + 1);
  };

  const prevSlide = () => {
    setCurrentIndex(currentIndex === 0 ? testimonials?.length - 1 : currentIndex - 1);
  };

  const currentTestimonial = testimonials?.[currentIndex];

  return (
    <section className="py-24 bg-card/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 border border-accent/20 rounded-full px-4 py-2 mb-6">
            <Icon name="Users" size={16} className="text-accent" />
            <span className="text-sm font-medium text-accent">
              Customer Stories
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Trusted by developers
            <span className="block text-accent">worldwide</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Join thousands of developers and companies who have built amazing messaging 
            experiences with MessagingHub Pro.
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="relative max-w-4xl mx-auto mb-16">
          <div className="bg-card border border-border rounded-2xl p-8 lg:p-12 shadow-elevation-2">
            {/* Quote Icon */}
            <div className="flex justify-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="Quote" size={32} className="text-primary" />
              </div>
            </div>

            {/* Testimonial Content */}
            <blockquote className="text-xl lg:text-2xl text-foreground text-center leading-relaxed mb-8">
              "{currentTestimonial?.content}"
            </blockquote>

            {/* Author Info */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-8">
              <div className="flex items-center space-x-4">
                <Image
                  src={currentTestimonial?.avatar}
                  alt={currentTestimonial?.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div className="text-center sm:text-left">
                  <div className="font-semibold text-foreground">
                    {currentTestimonial?.name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {currentTestimonial?.role}
                  </div>
                  <div className="text-sm font-medium text-primary">
                    {currentTestimonial?.company}
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden sm:block w-px h-12 bg-border"></div>

              {/* Metrics */}
              <div className="text-center">
                <div className="text-2xl font-bold text-accent">
                  {currentTestimonial?.metrics}
                </div>
                <div className="text-sm text-muted-foreground">
                  {currentTestimonial?.useCase}
                </div>
              </div>
            </div>

            {/* Rating */}
            <div className="flex justify-center space-x-1 mb-8">
              {[...Array(5)]?.map((_, i) => (
                <Icon
                  key={i}
                  name="Star"
                  size={20}
                  className="text-yellow-400 fill-current"
                />
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-background border border-border rounded-full flex items-center justify-center hover:bg-muted transition-colors duration-200 shadow-elevation-1"
          >
            <Icon name="ChevronLeft" size={20} className="text-foreground" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-background border border-border rounded-full flex items-center justify-center hover:bg-muted transition-colors duration-200 shadow-elevation-1"
          >
            <Icon name="ChevronRight" size={20} className="text-foreground" />
          </button>
        </div>

        {/* Testimonial Indicators */}
        <div className="flex justify-center space-x-2 mb-16">
          {testimonials?.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-200 ${
                index === currentIndex
                  ? 'bg-primary scale-125' :'bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
            />
          ))}
        </div>

        {/* Company Logos */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-8">
            Trusted by leading companies across industries
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            {companies?.map((company, index) => (
              <div key={index} className="flex items-center space-x-3">
                <Icon name={company?.logo} size={24} className="text-muted-foreground" />
                <span className="text-lg font-semibold text-muted-foreground">
                  {company?.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <div className="bg-card border border-border rounded-xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-semibold text-foreground mb-4">
              Ready to join them?
            </h3>
            <p className="text-muted-foreground mb-6">
              Start building your messaging solution today and see why developers love our platform.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => window.location.href = '/developer-portal'}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200"
              >
                <Icon name="Code" size={18} />
                <span>Start Free Trial</span>
              </button>
              <button
                onClick={() => window.location.href = '/contact-sales'}
                className="inline-flex items-center space-x-2 px-6 py-3 border border-border rounded-lg text-foreground hover:bg-muted/50 transition-colors duration-200"
              >
                <Icon name="MessageCircle" size={18} />
                <span>Talk to Sales</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;